import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BankAccount, BankStatementMonthlySummary } from 'app/models/dto/bank-account.model';
import { MatDialog } from '@angular/material/dialog';
import isEqual from 'lodash/isEqual';
import { AccountDeleteDialogComponent } from '../account-delete-dialog/account-delete-dialog.component';
import { Alert } from 'app/models/dto/alert.model';
import { DocumentLinkInfo } from 'app/models/dto/document.model';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';

interface EditAccountModel {
  id: number;
  accountNumber: number;
  accountType: string;
  aggregatorName?: string;
  bank_statement_monthly_summaries?: BankStatementMonthlySummary[];
}

@Component({
  selector: 'app-bank-account',
  templateUrl: './bank-account.component.html',
  styleUrls: ['./bank-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankAccountComponent implements OnInit, OnChanges {
  @Input() editingBankAccountId: number;
  @Input() bankAccount: BankAccount;
  @Input() cloning: boolean;
  @Input() alert: { level: string, message: string } | null;
  @Input() documentAlert: { level: string, message: string } | null;
  @Input() borrowerId: number;
  @Input() originalAccount: any;
  @Input() documentLinkInfo: DocumentLinkInfo;
  @Input() newFdTableView: boolean = false;
  @Input() bankValues: BorrowerValue[];
  @Output() addingAccount = new EventEmitter<void>();
  @Output() addingAccountModal = new EventEmitter<void>();
  @Output() editAccount = new EventEmitter<BankAccount>();
  @Output() saveAccount = new EventEmitter<BankAccount>();
  @Output() cancelEditAccount = new EventEmitter<number>();
  @Output() editSummaries = new EventEmitter<BankStatementMonthlySummary[]>();
  @Output() editAccountAndSummaries = new EventEmitter<BankAccount>();
  @Output() createMonthlySummary = new EventEmitter<BankStatementMonthlySummary>();
  @Output() removeBankAccount = new EventEmitter<BankAccount>();

  form = new UntypedFormGroup({});
  model: BankAccount;
  initialModel: BankAccount;
  fields: FormlyFieldConfig[] = [];
  editingAccount = false;
  saving = false;
  retreiveMonthlySummaries = false;
  isDeletingAccount = false;
  showOriginalAccount: boolean;
  originalAccountNumber: number;
  originalAccountType: string;


  constructor(private dialog: MatDialog) { }

  ngOnInit(): void {
    this.alert = null;
    const { accountNumber, accountType } = this.bankAccount;
    this.model = {
      id: this.bankAccount.id,
      accountNumber,
      accountType
    };
    this.initialModel = { ...this.model };
    this.fields = [
      {
        key: 'accountNumber',
        type: 'text',
        templateOptions: {
          label: 'Account Number',
          required: true,
        }
      },
      {
        key: 'accountType',
        type: 'select',
        className: 'rightColumn',
        templateOptions: {
          label: 'Account Type',
          required: true,
          options: [
            { label: 'Checking', value: 'checking' },
            { label: 'Savings', value: 'savings' },
          ],
        }
      }
    ];
  }

  ngOnChanges(): void {
    this.editingAccount = this.editingBankAccountId === this.bankAccount.id;
  }

  save(): void {
    this.retreiveMonthlySummaries = true;
  }

  onDocumentError(alert: Alert): void {
    this.alert = alert;
  }

  onEditAccountOrSummaries(summaries: BankStatementMonthlySummary[]): void {
    const accountChanged = !isEqual(this.model, this.initialModel);
    const summariesChanged = summaries.length > 0;
    if (!accountChanged && !summariesChanged) {
      this.retreiveMonthlySummaries = false;
      return;
    }
    this.saveAccount.emit(this.model);
    this.saving = true;
    if (accountChanged && summariesChanged) {
      this.model.bank_statement_monthly_summaries = summaries;
      this.editAccountAndSummaries.emit(this.model);
    } else if (accountChanged) {
      this.editAccount.emit(this.model);
    } else if (summariesChanged) {
      this.editSummaries.emit(summaries);
    }
  }

  onCreateMonthlySummary(summary: BankStatementMonthlySummary): void {
    summary.bankStatementAccountId = this.bankAccount.id;
    this.createMonthlySummary.emit(summary);
  }

  onShowOriginalAccount(originalAccount: BankAccount): void {
    if (originalAccount) {
      this.showOriginalAccount = true;
      this.originalAccountNumber = originalAccount.accountNumber;
      this.originalAccountType = originalAccount.accountType;
    } else {
      this.showOriginalAccount = false;
    }
  }

  onCancel(): void {
    this.editingAccount = false;
    this.cancelEditAccount.emit();
  }

  startAccountEdit(): void {
    this.editAccount.emit(this.model);
    if (!this.bankAccount.aggregatorName) {
      this.editingAccount = true;
    }
  }

  removeAccount(): void {
    this.dialog
      .open(AccountDeleteDialogComponent, { data: this.bankAccount })
      .afterClosed().subscribe((shouldDelete) => {
        if (shouldDelete) {
          this.isDeletingAccount = true;
          this.removeBankAccount.emit(this.bankAccount);
        }
      });
  }
}
